"use client";

import React, { useEffect, useState } from "react";
import { Cpu, Info, Laptop, Layers, Smartphone, Sparkles, Users, Wallet } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
interface ComparisonRow {
  key: string;
  labelVi: string;
  labelEn: string;
  icon: React.ElementType;
  duploVi: string;
  duploEn: string;
  whalesbotVi: string;
  whalesbotEn: string;
  isCost?: boolean;
}
const COMPARISON_DATA: ComparisonRow[] = [{
  key: "kit",
  labelVi: "Bộ kit",
  labelEn: "Robotics Kit",
  icon: Layers,
  duploVi: "Duplo (LEGO® Education)",
  duploEn: "Duplo (LEGO® Education)",
  whalesbotVi: "WhalesBot AI Module 1S",
  whalesbotEn: "WhalesBot AI Module 1S"
}, {
  key: "computer",
  labelVi: "Máy tính",
  labelEn: "Computer",
  icon: Laptop,
  duploVi: "Không bắt buộc",
  duploEn: "Not required",
  whalesbotVi: "Cần thiết",
  whalesbotEn: "Required"
}, {
  key: "tablet",
  labelVi: "Tablet",
  labelEn: "Tablet",
  icon: Smartphone,
  duploVi: "Có thể bổ sung (tùy chọn)",
  duploEn: "Optional supplementary device",
  whalesbotVi: "Không phải yêu cầu chính",
  whalesbotEn: "Not a primary requirement"
}, {
  key: "ideal_ratio",
  labelVi: "Học sinh / kit (mô hình lý tưởng)",
  labelEn: "Students per kit (ideal model)",
  icon: Users,
  duploVi: "2 học sinh/kit",
  duploEn: "2 students/kit",
  whalesbotVi: "2 học sinh/kit",
  whalesbotEn: "2 students/kit"
}, {
  key: "cost_ratio",
  labelVi: "Tối ưu chi phí",
  labelEn: "Cost-optimized model",
  icon: Users,
  duploVi: "3 học sinh/kit",
  duploEn: "3 students/kit",
  whalesbotVi: "3 học sinh/kit",
  whalesbotEn: "3 students/kit"
}, {
  key: "cost",
  labelVi: "Chi phí thiết bị tham khảo",
  labelEn: "Reference equipment cost",
  icon: Wallet,
  duploVi: "Khoảng 1.000.000 VNĐ/bộ",
  duploEn: "Approx. 1,000,000 VND/kit",
  whalesbotVi: "Khoảng 6.500.000 VNĐ/bộ",
  whalesbotEn: "Approx. 6,500,000 VND/kit",
  isCost: true
}];
export default function EquipmentComparison() {
  const [lang, setLang] = useState<"vi" | "en">("vi");
  useEffect(() => {
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "vi" || stored === "en") {
        setLang(stored);
      }
    } catch {
      // localStorage unavailable in restricted contexts
    }
    const handleStorage = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, []);
  const isVi = lang === "vi";
  return <section id="equipmentcomparison" data-nav="light" className="relative overflow-hidden bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto mb-10 max-w-3xl text-center md:mb-14">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-secondary/40 bg-secondary/10 px-3.5 py-1 text-xs font-semibold text-secondary-foreground">
            <span className="size-2 rounded-full bg-secondary" />
            <span>
              {isVi ? "So sánh mô hình & thiết bị đào tạo" : "Equipment & Classroom Model Comparison"}
            </span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {isVi ? "Phân biệt yêu cầu thiết bị giữa hai lộ trình" : "Understanding Equipment Differences Across Tracks"}
          </h2>

          <p className="mt-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
            {isVi ? "Giúp phụ huynh nắm rõ cấu hình phần cứng, thiết bị tương tác và mô hình lớp học giữa giai đoạn khám phá nền tảng D1–D4 và giai đoạn lập trình nâng cao HP1–HP6." : "Helping parents easily distinguish the hardware, screen requirements, and classroom group setups between the foundational junior track (D1–D4) and advanced robotics track (HP1–HP6)."}
          </p>
        </div>

        {/* Side-by-Side Equipment Visual Showcase */}
        <div className="mx-auto mb-10 grid max-w-5xl grid-cols-1 gap-6 md:mb-12 md:grid-cols-2">
          {/* Duplo Kit Visual Card */}
          <div className="group overflow-hidden rounded-xl border border-border bg-card p-4 shadow-xs transition-all hover:border-chart-1/50">
            <div className="relative mb-3 aspect-[4/3] w-full overflow-hidden rounded-lg bg-muted/30">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/duplo_robotics_kit.png-wvc-sizes" alt="Duplo LEGO Education Kit" loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute top-3 left-3">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card/90 px-2.5 py-1 text-xs font-semibold text-foreground backdrop-blur-xs">
                  <span className="size-2 rounded-full bg-chart-1" />
                  D1–D4 | Duplo
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-foreground">
                  {isVi ? "Bộ Duplo (LEGO® Education)" : "Duplo (LEGO® Education) Kit"}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {isVi ? "Khám phá tư duy & cơ khí cơ bản" : "Junior hardware & early mechanics"}
                </p>
              </div>
              <Badge variant="outline" className="text-xs">
                {isVi ? "Chi phí ~1TR" : "~1M VND"}
              </Badge>
            </div>
          </div>

          {/* WhalesBot AI Module 1S Visual Card */}
          <div className="group overflow-hidden rounded-xl border border-secondary/40 bg-card p-4 shadow-xs transition-all hover:border-secondary">
            <div className="relative mb-3 aspect-[4/3] w-full overflow-hidden rounded-lg bg-muted/30">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_ai_module_1s_kit.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_ai_module_1s_kit.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/whalesbot_ai_module_1s_kit.png-wvc-sizes" alt="WhalesBot AI Module 1S Kit" loading="lazy" className="size-full object-cover transition-transform duration-300 group-hover:scale-105" />
              <div className="absolute top-3 left-3">
                <span className="inline-flex items-center gap-1.5 rounded-md border border-secondary/40 bg-card/90 px-2.5 py-1 text-xs font-semibold text-secondary-foreground backdrop-blur-xs">
                  <span className="size-2 rounded-full bg-secondary" />
                  HP1–HP6 | WhalesBot
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-foreground">
                  {isVi ? "Bộ WhalesBot AI Module 1S" : "WhalesBot AI Module 1S Kit"}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {isVi ? "Lập trình AI & cảm biến thông minh" : "Smart sensors & AI coding"}
                </p>
              </div>
              <Badge variant="secondary" className="bg-secondary/20 text-xs text-secondary-foreground">
                {isVi ? "Chi phí ~6.5TR" : "~6.5M VND"}
              </Badge>
            </div>
          </div>
        </div>

        {/* Disclaimer Alert */}
        <div className="mx-auto mb-10 max-w-4xl md:mb-12">
          <Alert className="border-border bg-card shadow-xs">
            <Info className="size-5 text-secondary" />
            <AlertTitle className="text-sm font-semibold text-foreground">
              {isVi ? "Lưu ý quan trọng về chi phí thiết bị" : "Important Notice Concerning Equipment Costs"}
            </AlertTitle>
            <AlertDescription className="text-xs leading-relaxed text-muted-foreground sm:text-sm">
              {isVi ? <>
                  Bảng thông tin dưới đây <strong>KHÔNG</strong> phải là bảng giá học phí
                  khóa học. Mọi định mức giá thành được nêu rõ là{" "}
                  <span className="font-semibold text-foreground">
                    chi phí thiết bị tham khảo
                  </span>
                  , tách biệt hoàn toàn với biểu phí đào tạo và học phí giảng dạy
                  chính thức tại học viện.
                </> : <>
                  This table is <strong>NOT</strong> a course tuition fee schedule.
                  All figures shown here are explicitly categorized as{" "}
                  <span className="font-semibold text-foreground">
                    reference equipment cost
                  </span>
                  , completely separate from curriculum tuition fees which are not listed here.
                </>}
            </AlertDescription>
          </Alert>
        </div>

        {/* Desktop Comparison Table (Hidden on small screens) */}
        <div className="hidden overflow-hidden rounded-xl border border-border bg-card shadow-xs md:block">
          <Table>
            <TableHeader className="bg-muted/40">
              <TableRow className="border-b border-border">
                <TableHead className="w-[34%] py-4 pl-6 text-sm font-semibold text-foreground">
                  {isVi ? "Hạng mục so sánh" : "Comparison Criteria"}
                </TableHead>
                <TableHead className="w-[33%] py-4 text-sm font-semibold text-foreground">
                  <div className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-chart-1" />
                    <span>D1–D4 | Duplo</span>
                  </div>
                  <span className="block text-xs font-normal text-muted-foreground">
                    {isVi ? "Giai đoạn mầm non & khám phá" : "Foundation & Junior Stage"}
                  </span>
                </TableHead>
                <TableHead className="w-[33%] py-4 pr-6 text-sm font-semibold text-foreground">
                  <div className="flex items-center gap-2">
                    <span className="size-2 rounded-full bg-secondary" />
                    <span>HP1–HP6 | WhalesBot AI Module 1S</span>
                  </div>
                  <span className="block text-xs font-normal text-muted-foreground">
                    {isVi ? "Giai đoạn robot & thuật toán AI" : "Robotics & AI Track"}
                  </span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {COMPARISON_DATA.map((row, i) => {
              const IconComponent = row.icon;
              return <TableRow key={row.key} data-index={i} className={`border-b border-border transition-colors hover:bg-muted/30 ${row.isCost ? "bg-muted/10 font-medium" : ""}`}>
                    <TableCell className="py-4 pl-6 align-middle font-medium text-foreground">
                      <div className="flex items-center gap-3">
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
                          <IconComponent className="size-4 text-foreground" />
                        </div>
                        <div>
                          <span className="block text-sm">
                            {isVi ? row.labelVi : row.labelEn}
                          </span>
                          {row.isCost && <span className="text-xs text-muted-foreground">
                              {isVi ? "Ước tính mua bộ kit" : "Estimated per hardware kit"}
                            </span>}
                        </div>
                      </div>
                    </TableCell>

                    <TableCell className="py-4 align-middle text-sm text-foreground">
                      {row.isCost ? <div className="inline-block rounded-md border border-border bg-card px-3 py-1.5 shadow-2xs">
                          <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                            {isVi ? "Chi phí thiết bị tham khảo" : "Reference equipment cost"}
                          </span>
                          <span className="text-sm font-semibold text-foreground">
                            {isVi ? row.duploVi : row.duploEn}
                          </span>
                        </div> : <span className="text-sm">
                          {isVi ? row.duploVi : row.duploEn}
                        </span>}
                    </TableCell>

                    <TableCell className="py-4 pr-6 align-middle text-sm text-foreground">
                      {row.isCost ? <div className="inline-block rounded-md border border-secondary/40 bg-secondary/10 px-3 py-1.5 shadow-2xs">
                          <span className="block text-xs uppercase tracking-wider text-secondary-foreground/80">
                            {isVi ? "Chi phí thiết bị tham khảo" : "Reference equipment cost"}
                          </span>
                          <span className="text-sm font-semibold text-secondary-foreground">
                            {isVi ? row.whalesbotVi : row.whalesbotEn}
                          </span>
                        </div> : <span className="text-sm font-medium">
                          {isVi ? row.whalesbotVi : row.whalesbotEn}
                        </span>}
                    </TableCell>
                  </TableRow>;
            })}
            </TableBody>
          </Table>
        </div>

        {/* Mobile & Tablet Card Grid (Visible on smaller screens) */}
        <div className="grid gap-6 md:hidden sm:grid-cols-2">
          {/* Track 1: D1-D4 */}
          <Card className="border border-border bg-card shadow-xs">
            <CardHeader className="border-b border-border pb-4">
              <div className="mb-2 flex items-center justify-between">
                <Badge variant="outline" className="text-xs text-muted-foreground">
                  {isVi ? "Giai đoạn 1" : "Stage 1"}
                </Badge>
                <span className="size-2 rounded-full bg-chart-1" />
              </div>
              <CardTitle className="text-xl font-bold text-foreground">
                D1–D4 | Duplo
              </CardTitle>
              <CardDescription className="text-xs text-muted-foreground">
                {isVi ? "Mô hình trực quan phát triển tư duy mở đầu" : "Foundational screen-free introductory modeling"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {COMPARISON_DATA.map((row, i) => {
              const IconComponent = row.icon;
              return <div key={row.key} data-index={i} className={`rounded-lg border p-3 ${row.isCost ? "border-border bg-muted/30" : "border-border/60 bg-background/50"}`}>
                    <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <IconComponent className="size-3.5 text-foreground" />
                      <span>{isVi ? row.labelVi : row.labelEn}</span>
                    </div>
                    <div className="text-sm font-semibold text-foreground">
                      {isVi ? row.duploVi : row.duploEn}
                    </div>
                    {row.isCost && <span className="mt-1 block text-[11px] text-muted-foreground">
                        {isVi ? "Ghi chú: Chi phí thiết bị tham khảo (không phải học phí)" : "Note: Reference equipment cost (not tuition)"}
                      </span>}
                  </div>;
            })}
            </CardContent>
          </Card>

          {/* Track 2: HP1-HP6 */}
          <Card className="border border-secondary/40 bg-card shadow-xs">
            <CardHeader className="border-b border-border pb-4">
              <div className="mb-2 flex items-center justify-between">
                <Badge variant="secondary" className="bg-secondary/20 text-xs text-secondary-foreground">
                  {isVi ? "Giai đoạn 2" : "Stage 2"}
                </Badge>
                <span className="size-2 rounded-full bg-secondary" />
              </div>
              <CardTitle className="text-xl font-bold text-foreground">
                HP1–HP6 | WhalesBot
              </CardTitle>
              <CardDescription className="text-xs text-muted-foreground">
                {isVi ? "WhalesBot AI Module 1S & phần mềm chuyên sâu" : "WhalesBot AI Module 1S hardware & algorithmic coding"}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4 pt-4">
              {COMPARISON_DATA.map((row, i) => {
              const IconComponent = row.icon;
              return <div key={row.key} data-index={i} className={`rounded-lg border p-3 ${row.isCost ? "border-secondary/50 bg-secondary/10" : "border-border/60 bg-background/50"}`}>
                    <div className="mb-1 flex items-center gap-2 text-xs text-muted-foreground">
                      <IconComponent className="size-3.5 text-secondary-foreground" />
                      <span>{isVi ? row.labelVi : row.labelEn}</span>
                    </div>
                    <div className="text-sm font-semibold text-foreground">
                      {isVi ? row.whalesbotVi : row.whalesbotEn}
                    </div>
                    {row.isCost && <span className="mt-1 block text-[11px] font-medium text-secondary-foreground">
                        {isVi ? "Ghi chú: Chi phí thiết bị tham khảo (không phải học phí)" : "Note: Reference equipment cost (not tuition)"}
                      </span>}
                  </div>;
            })}
            </CardContent>
          </Card>
        </div>

        {/* Bottom Context Callout */}
        <div className="mt-8 rounded-xl border border-border bg-card p-4 text-center sm:p-5">
          <p className="text-xs text-muted-foreground sm:text-sm">
            {isVi ? <>
                Mô hình lớp học tiêu chuẩn tại ROBOTO duy trì tỷ lệ tối ưu{" "}
                <span className="font-semibold text-foreground">2 học sinh/kit</span>{" "}
                để đảm bảo khả năng thực hành tương tác tối đa của mỗi học viên.
              </> : <>
                Standard classroom settings at ROBOTO adhere to the optimal ratio of{" "}
                <span className="font-semibold text-foreground">
                  2 students per kit
                </span>{" "}
                to ensure maximum hands-on interaction for every learner.
              </>}
          </p>
        </div>
      </div>
    </section>;
}